import { useEffect, useMemo, useRef, useState } from 'react'
import './DeckThumbnail.css'

// Desktop "design" viewport the preview iframe renders at. The decks' CSS is
// vw/vh-based, so we render at a real desktop width inside the iframe (its own
// viewport) and visually scale the whole iframe down to the card width.
const DESIGN_W = 1280
const DESIGN_H = 800

interface DeckThumbnailProps {
  slug: string
  title: string
}

export function DeckThumbnail({ slug, title }: DeckThumbnailProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0)

  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0
      if (w > 0) setScale(w / DESIGN_W)
    })
    ro.observe(wrap)
    return () => ro.disconnect()
  }, [])

  // Only the transform is dynamic; the fixed design size stays constant.
  const iframeStyle = useMemo<React.CSSProperties>(
    () => ({
      width: DESIGN_W,
      height: DESIGN_H,
      transform: `scale(${scale})`,
    }),
    [scale],
  )

  return (
    <div className="deck-thumb" ref={wrapRef}>
      {scale > 0 && (
        <iframe
          className="deck-thumb-frame"
          style={iframeStyle}
          src={`/preview/${slug}`}
          title={`${title} preview`}
          loading="lazy"
          tabIndex={-1}
          aria-hidden="true"
        />
      )}
    </div>
  )
}
